import type { Dapp } from '@src/dto';
import type { Release } from '@src/dto/Release';
import type { StatusPillTone } from '../../portal';
import {
  getReleaseStatusDisplay,
  isReleaseWaitingToGoLive,
} from '../../../utils/releaseStatusConfig';
import {
  countDraftChanges,
  type StoreListingDraft,
} from '../../../utils/storeListingDraft';
import { getDappMissingRequiredListingFields } from '../../../utils/storeListingCompleteness';
import { formatShortDate } from '../../../utils/stringHelpers';

/** Store publication state (`useDappStoreStatus`); null when unknown. */
export type StoreStatus = 'published' | 'unpublished' | null;

export interface ReleaseSummaryContext {
  publishedVersionCode?: number | null;
  storeStatus?: StoreStatus;
}

export interface SummaryColumn {
  label: string;
  value: string;
  /** Muted (seeker-400) value, used for "None". */
  valueMuted?: boolean;
  pillTone: StatusPillTone;
  pillLabel: string;
  pillIcon?: 'eye' | 'pencil' | 'clock';
  caption: string;
}

type SummaryDapp = Pick<
  Dapp,
  'publishedVersionName' | 'publishedVersionCode' | 'status'
>;

const newestFirst = (a: Release, b: Release) =>
  new Date(b.submittedAt ?? b.created).getTime() -
  new Date(a.submittedAt ?? a.created).getTime();

/** Nothing is live while the Store lists the app as unpublished ("waiting to go live"). */
export function findLiveRelease(
  releases: Release[],
  dapp: SummaryDapp | null,
  context: ReleaseSummaryContext = {},
): Release | null {
  if (context.storeStatus === 'unpublished') return null;
  return (
    releases.find((release) => release.isLive) ??
    (dapp?.publishedVersionCode != null
      ? releases.find(
          (release) => release.versionCode === dapp.publishedVersionCode,
        )
      : undefined) ??
    null
  );
}

export function getLiveVersionSummary(
  releases: Release[],
  dapp: SummaryDapp | null,
  context: ReleaseSummaryContext = {},
): SummaryColumn {
  const live = findLiveRelease(releases, dapp, context);
  const versionName =
    live?.versionName ??
    (context.storeStatus === 'unpublished'
      ? undefined
      : dapp?.publishedVersionName);
  if (!versionName) {
    return {
      label: 'Live Version',
      value: 'None',
      valueMuted: true,
      pillTone: 'neutral',
      pillLabel: 'Not live',
      caption:
        releases.length > 0
          ? 'Not live in the dApp Store.'
          : 'Upload your first release.',
    };
  }
  const since = formatShortDate(live?.publishedAt ?? live?.submittedAt);
  return {
    label: 'Live Version',
    value: `v${versionName}`,
    pillTone: 'live',
    pillLabel: 'Live',
    caption: since ? `Live since ${since}` : 'Live in the dApp Store.',
  };
}

export function getNextReleaseSummary(
  releases: Release[],
  context: ReleaseSummaryContext,
): SummaryColumn {
  const label = 'Next Release';
  const waiting = releases.find((release) =>
    isReleaseWaitingToGoLive(release, context),
  );
  if (waiting) {
    return {
      label,
      value: `v${waiting.versionName ?? '?'}`,
      pillTone: 'waiting',
      pillLabel: 'Waiting to go live',
      pillIcon: 'clock',
      caption: 'Approved and ready to publish.',
    };
  }

  // Same resolver as the Releases table, so superseded/deprecated rows never show here.
  const inReview = releases
    .filter(
      (release) =>
        getReleaseStatusDisplay(release, context).label === 'In Review',
    )
    .sort(newestFirst)[0];
  if (inReview) {
    const submitted = formatShortDate(inReview.submittedAt ?? inReview.created);
    return {
      label,
      value: `v${inReview.versionName ?? '?'}`,
      pillTone: 'review',
      pillLabel: 'In Review',
      pillIcon: 'eye',
      caption: submitted ? `Submitted ${submitted}` : 'Awaiting review.',
    };
  }

  return {
    label,
    value: 'None',
    valueMuted: true,
    pillTone: 'neutral',
    pillLabel: 'Nothing in review',
    caption: 'You are all caught up.',
  };
}

export function getStoreListingSummary(
  draft: StoreListingDraft | null,
  dapp: Dapp | null,
): SummaryColumn {
  const label = 'Store Listing';
  const changes = countDraftChanges(draft, dapp);
  if (draft && changes > 0) {
    return {
      label,
      value: `${changes} ${changes === 1 ? 'update' : 'updates'}`,
      pillTone: 'unpublished',
      pillLabel: 'Unpublished',
      pillIcon: 'pencil',
      caption: `Edited ${formatShortDate(draft.savedAt)}`,
    };
  }

  const missing = dapp ? getDappMissingRequiredListingFields(dapp).length : 0;
  if (missing > 0) {
    return {
      label,
      value: `${missing} missing`,
      pillTone: 'required',
      pillLabel: 'Incomplete',
      caption: 'Finish your listing to publish.',
    };
  }

  return {
    label,
    value: 'Up to date',
    pillTone: 'published',
    pillLabel: 'Published',
    caption: 'All listing changes are live.',
  };
}

export function getDappStatusPill(
  dapp: Pick<Dapp, 'status'>,
  hasLiveRelease: boolean,
  storeStatus: StoreStatus = null,
): { tone: StatusPillTone; label: string } {
  if (storeStatus === 'unpublished') {
    return { tone: 'unpublished', label: 'Unpublished' };
  }
  if (dapp.status === 'Published' || hasLiveRelease) {
    return { tone: 'live', label: 'Live' };
  }
  switch (dapp.status) {
    case 'UnderReview':
      return { tone: 'review', label: 'In Review' };
    case 'Rejected':
      return { tone: 'rejected', label: 'Rejected' };
    case 'Archived':
      return { tone: 'neutral', label: 'Archived' };
    default:
      return { tone: 'neutral', label: 'Draft' };
  }
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * "{n} this week" is only exact when the newest-first page did not overflow
 * the window; returns null when the true count could be larger.
 */
export function countReviewsThisWeek(
  reviews: { createdAt: string }[],
  pageSize: number,
  now = Date.now(),
): number | null {
  const inWindow = reviews.filter(
    (review) => now - new Date(review.createdAt).getTime() <= WEEK_MS,
  ).length;
  if (reviews.length >= pageSize && inWindow === reviews.length) {
    return null;
  }
  return inWindow;
}

const compact = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 2,
});

export function formatCompactNumber(value: number) {
  return compact.format(value);
}
